import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  LinearProgress,
  Fade,
  Portal,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Close as CloseIcon, KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import { useTour } from '../../context/TourContext';

interface TooltipPosition {
  top: number;
  left: number;
  placement: 'top' | 'bottom' | 'left' | 'right';
}

/**
 * FeatureTour component - renders the active tour with spotlight effect
 */
export const FeatureTour: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { activeTour, currentStepIndex, nextStep, prevStep, skipTour, endTour } = useTour();
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<TooltipPosition | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const currentStep = activeTour?.steps[currentStepIndex];
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = activeTour ? currentStepIndex === activeTour.steps.length - 1 : false;
  const progress = activeTour ? ((currentStepIndex + 1) / activeTour.steps.length) * 100 : 0;

  // Find and highlight target element
  const updateTargetPosition = useCallback(() => {
    if (!currentStep?.target) {
      setTargetRect(null);
      return;
    }

    const element = document.querySelector(currentStep.target);
    if (element) {
      const rect = element.getBoundingClientRect();
      setTargetRect(rect);
    } else {
      setTargetRect(null);
    }
  }, [currentStep]);

  // Calculate tooltip position
  const updateTooltipPosition = useCallback(() => {
    if (!targetRect || !tooltipRef.current) return;

    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const padding = 16;
    const spacing = 12;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Determine best placement
    let placement = currentStep?.placement || 'bottom';
    let top = 0;
    let left = 0;

    // Calculate positions for each placement
    const positions = {
      bottom: {
        top: targetRect.bottom + spacing,
        left: targetRect.left + targetRect.width / 2 - tooltipRect.width / 2,
        fits: targetRect.bottom + spacing + tooltipRect.height < viewportHeight - padding,
      },
      top: {
        top: targetRect.top - tooltipRect.height - spacing,
        left: targetRect.left + targetRect.width / 2 - tooltipRect.width / 2,
        fits: targetRect.top - spacing - tooltipRect.height > padding,
      },
      right: {
        top: targetRect.top + targetRect.height / 2 - tooltipRect.height / 2,
        left: targetRect.right + spacing,
        fits: targetRect.right + spacing + tooltipRect.width < viewportWidth - padding,
      },
      left: {
        top: targetRect.top + targetRect.height / 2 - tooltipRect.height / 2,
        left: targetRect.left - tooltipRect.width - spacing,
        fits: targetRect.left - spacing - tooltipRect.width > padding,
      },
    };

    // Use preferred placement if it fits, otherwise find one that does
    if (positions[placement].fits) {
      top = positions[placement].top;
      left = positions[placement].left;
    } else {
      // Try other placements
      const fallbackOrder: Array<'bottom' | 'top' | 'right' | 'left'> = [
        'bottom',
        'top',
        'right',
        'left',
      ];
      for (const p of fallbackOrder) {
        if (positions[p].fits) {
          placement = p;
          top = positions[p].top;
          left = positions[p].left;
          break;
        }
      }
    }

    // Ensure tooltip stays within viewport
    left = Math.max(padding, Math.min(left, viewportWidth - tooltipRect.width - padding));
    top = Math.max(padding, Math.min(top, viewportHeight - tooltipRect.height - padding));

    setTooltipPosition({ top, left, placement });
  }, [targetRect, currentStep?.placement]);

  // Update positions on step change and resize
  useEffect(() => {
    updateTargetPosition();

    const handleResize = () => {
      updateTargetPosition();
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize, true);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize, true);
    };
  }, [updateTargetPosition]);

  // Update tooltip position after target rect changes
  useEffect(() => {
    if (targetRect) {
      // Small delay to allow tooltip to render first
      const timer = setTimeout(updateTooltipPosition, 50);
      return () => clearTimeout(timer);
    }
  }, [targetRect, updateTooltipPosition]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!activeTour) return;

      switch (e.key) {
        case 'ArrowRight':
        case 'Enter':
          e.preventDefault();
          nextStep();
          break;
        case 'ArrowLeft':
          if (!isFirstStep) {
            e.preventDefault();
            prevStep();
          }
          break;
        case 'Escape':
          e.preventDefault();
          skipTour();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeTour, nextStep, prevStep, skipTour, isFirstStep]);

  if (!activeTour || !currentStep) {
    return null;
  }

  return (
    <Portal>
      {/* Backdrop overlay */}
      <Fade in>
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: theme.zIndex.modal,
            pointerEvents: 'none',
          }}
        >
          {/* Dark overlay with cutout for target */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              // Create spotlight cutout using box-shadow
              ...(targetRect && {
                clipPath: `polygon(
                  0% 0%,
                  0% 100%,
                  ${targetRect.left - 8}px 100%,
                  ${targetRect.left - 8}px ${targetRect.top - 8}px,
                  ${targetRect.right + 8}px ${targetRect.top - 8}px,
                  ${targetRect.right + 8}px ${targetRect.bottom + 8}px,
                  ${targetRect.left - 8}px ${targetRect.bottom + 8}px,
                  ${targetRect.left - 8}px 100%,
                  100% 100%,
                  100% 0%
                )`,
              }),
            }}
          />

          {/* Target highlight border */}
          {targetRect && (
            <Box
              sx={{
                position: 'absolute',
                top: targetRect.top - 8,
                left: targetRect.left - 8,
                width: targetRect.width + 16,
                height: targetRect.height + 16,
                border: '3px solid',
                borderColor: 'primary.main',
                borderRadius: 2,
                boxShadow: '0 0 0 4px rgba(102, 44, 139, 0.3)',
                pointerEvents: 'none',
              }}
            />
          )}
        </Box>
      </Fade>

      {/* Tooltip */}
      <Fade in>
        <Paper
          ref={tooltipRef}
          elevation={8}
          sx={{
            position: 'fixed',
            top: tooltipPosition?.top ?? 0,
            left: tooltipPosition?.left ?? 0,
            zIndex: theme.zIndex.modal + 1,
            maxWidth: isMobile ? 'calc(100vw - 32px)' : 360,
            minWidth: isMobile ? 'calc(100vw - 32px)' : 300,
            borderRadius: 2,
            overflow: 'hidden',
            visibility: tooltipPosition ? 'visible' : 'hidden',
          }}
        >
          {/* Progress bar */}
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 3,
              bgcolor: 'grey.200',
              '& .MuiLinearProgress-bar': {
                bgcolor: 'primary.main',
              },
            }}
          />

          <Box sx={{ p: 2 }}>
            {/* Header */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                mb: 1,
              }}
            >
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Step {currentStepIndex + 1} of {activeTour.steps.length}
                </Typography>
                <Typography variant="subtitle1" fontWeight={600}>
                  {currentStep.title}
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={skipTour}
                sx={{ mt: -0.5, mr: -0.5 }}
                aria-label="Skip tour"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Content */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {currentStep.content}
            </Typography>

            {/* Tip */}
            {currentStep.tip && (
              <Box
                sx={{
                  bgcolor: 'rgba(102, 44, 139, 0.08)',
                  borderRadius: 1,
                  p: 1.5,
                  mb: 2,
                }}
              >
                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                  <strong>Tip:</strong> {currentStep.tip}
                </Typography>
              </Box>
            )}

            {/* Navigation */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Button
                size="small"
                onClick={prevStep}
                disabled={isFirstStep}
                startIcon={<KeyboardArrowLeft />}
                sx={{ visibility: isFirstStep ? 'hidden' : 'visible' }}
              >
                Back
              </Button>

              <Button
                variant="contained"
                size="small"
                onClick={isLastStep ? endTour : nextStep}
                endIcon={!isLastStep && <KeyboardArrowRight />}
              >
                {isLastStep ? 'Done' : 'Next'}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Fade>
    </Portal>
  );
};

export default FeatureTour;
